import React, { useContext, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import Header from "../Header";
import Footer from "../Footer";
import { ThemeContext } from "../../../Contaxt/ThemeContext";
import axiosIns, { API_URL } from "../../../config";
import "./techNews.css";

const INITIAL_FEEDS = {};

const parseDescription = (description = "") => {
    if (!description) {
        return { image: "", text: "" };
    }

    const doc = new DOMParser().parseFromString(description, "text/html");
    const image = doc.querySelector("img")?.getAttribute("src") || "";
    const text = doc.body.textContent?.replace(/\s+/g, " ").trim() || "";

    return { image, text };
};

const parseFeed = (xmlText) => {
    const xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
    const parserError = xmlDoc.querySelector("parsererror");

    if (parserError) {
        throw new Error("Unable to parse RSS feed.");
    }

    const channel = xmlDoc.querySelector("channel");
    const items = Array.from(xmlDoc.querySelectorAll("item"))
        .map((item, index) => {
            const description = item.querySelector("description")?.textContent || "";
            const mediaNode = item.getElementsByTagName("media:content")[0];
            const fallbackNode = item.getElementsByTagName("content")[0];
            const parsedDescription = parseDescription(description);
            const image =
                mediaNode?.getAttribute("url") ||
                fallbackNode?.getAttribute("url") ||
                parsedDescription.image;

            return {
                id: item.querySelector("guid")?.textContent || `${item.querySelector("link")?.textContent || "article"}-${index}`,
                title: item.querySelector("title")?.textContent?.trim() || "",
                link: item.querySelector("link")?.textContent?.trim() || "",
                description: parsedDescription.text,
                image,
                pubDate: item.querySelector("pubDate")?.textContent || "",
                author: item.getElementsByTagName("dc:creator")[0]?.textContent?.trim() || "",
            };
        })
        .filter((item) => item.title && item.link);

    return {
        title: channel?.querySelector("title")?.textContent?.trim() || "",
        description: channel?.querySelector("description")?.textContent?.trim() || "",
        lastBuildDate: channel?.querySelector("lastBuildDate")?.textContent || "",
        items,
    };
};

const formatDate = (dateValue) => {
    if (!dateValue) {
        return "";
    }

    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(parsedDate);
};

const getLocalDateKey = (dateValue) => {
    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
        return "";
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const slugifyFeedName = (value = "") =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const getBadgeLabel = (feedName = "") => {
    const normalizedName = feedName.toLowerCase();

    if (normalizedName.includes("artificial intelligence")) {
        return "AI NEWS";
    }

    if (normalizedName.includes("technology")) {
        return "TECH NEWS";
    }

    return "LIVE NEWS";
};

const FeedSliderSection = ({ feed, sectionTitle, badge, emptyMessage }) => {
    if (feed.isLoading) {
        return (
            <section className="tech-news-section">
                <div className="container">
                    <div className="tech-news-section__header">
                        <h2>{sectionTitle}</h2>
                    </div>
                    <div className="tech-news-state">
                        <p>Loading the latest headlines...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (!feed.items.length) {
        return null;
    }

    return (
        <section className="tech-news-section">
            <div className="container">
                <div className="tech-news-section__header">
                    <h2>{feed.title || sectionTitle}</h2>
                </div>

                <Swiper
                    modules={[Autoplay]}
                    className="tech-news-slider"
                    spaceBetween={24}
                    slidesPerView={1}
                    loop={feed.items.length > 1}
                    autoplay={
                        feed.items.length > 1
                            ? {
                                delay: 3200,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }
                            : false
                    }
                    breakpoints={{
                        576: {
                            slidesPerView: 1.2,
                            spaceBetween: 18,
                        },
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                        },
                        992: {
                            slidesPerView: 3,
                            spaceBetween: 22,
                        },
                        1200: {
                            slidesPerView: 4,
                            spaceBetween: 24,
                        },
                    }}
                >
                    {feed.items.map((article, index) => (
                        <SwiperSlide key={article.id || `${article.link}-${index}`}>
                            {/* <a
                                className="tech-news-card"
                                href={article.link}
                                target="_blank"
                                rel="noreferrer"
                            > */}
                                <div className="tech-news-card__image-wrap">
                                    <span className="tech-news-card__badge">{badge}</span>
                                    <img
                                        src={article.image || "/images/og-image.png"}
                                        alt={article.title}
                                        className="tech-news-card__image"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="tech-news-card__content">
                                    <h3>{article.title}</h3>
                                    <div className="tech-news-card__meta">
                                        <span>{formatDate(article.pubDate)}</span>
                                    </div>
                                </div>
                            {/* </a> */}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
};

const TechNews = () => {
    const { isLight } = useContext(ThemeContext);
    const [feedConfigs, setFeedConfigs] = useState([]);
    const [feeds, setFeeds] = useState(INITIAL_FEEDS);
    const [isConfigLoading, setIsConfigLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const todayKey = getLocalDateKey(new Date());

        const loadFeed = async (feedConfig) => {
            try {
                const response = await fetch(feedConfig.feedUrl);

                if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }

                const xmlText = await response.text();
                const parsedFeed = parseFeed(xmlText);
                const todayItems = parsedFeed.items
                    .filter((item) => getLocalDateKey(item.pubDate) === todayKey)
                    .slice(0, feedConfig.postLimit);

                if (!isMounted) {
                    return;
                }

                setFeeds((currentFeeds) => ({
                    ...currentFeeds,
                    [feedConfig.key]: {
                        title: parsedFeed.title || feedConfig.feedName,
                        description: parsedFeed.description,
                        lastBuildDate: parsedFeed.lastBuildDate,
                        items: todayItems,
                        isLoading: false,
                    },
                }));
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                setFeeds((currentFeeds) => ({
                    ...currentFeeds,
                    [feedConfig.key]: {
                        ...currentFeeds[feedConfig.key],
                        items: [],
                        isLoading: false,
                    },
                }));
            }
        };

        const loadFeedConfigs = async () => {
            try {
                const response = await axiosIns.get(API_URL.get_live_url);
                const feedRows = Array.isArray(response?.data?.data) ? response.data.data : [];

                if (!isMounted) {
                    return;
                }

                const mappedConfigs = feedRows.map((item, index) => ({
                    key: slugifyFeedName(item.feed_name) || `feed-${item.id || index}`,
                    feedName: item.feed_name || `Feed ${index + 1}`,
                    feedUrl: item.feed_url || "",
                    postLimit: Number(item.post_limit) > 0 ? Number(item.post_limit) : 20,
                    badge: getBadgeLabel(item.feed_name),
                    emptyMessage: `${item.feed_name || "This"} headlines are not available right now.`,
                })).filter((item) => item.feedUrl);

                setFeedConfigs(mappedConfigs);
                setFeeds(
                    mappedConfigs.reduce((accumulator, item) => {
                        accumulator[item.key] = {
                            title: item.feedName,
                            description: "",
                            lastBuildDate: "",
                            items: [],
                            isLoading: true,
                        };

                        return accumulator;
                    }, {})
                );
                setIsConfigLoading(false);

                mappedConfigs.forEach((feedConfig) => {
                    loadFeed(feedConfig);
                });
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                setFeedConfigs([]);
                setFeeds({});
                setIsConfigLoading(false);
            }
        };

        loadFeedConfigs();

        return () => {
            isMounted = false;
        };
    }, []);

    const visibleSections = feedConfigs.filter((feedConfig) => feeds[feedConfig.key]?.items.length > 0);
    const isLoadingAnyFeed = isConfigLoading || feedConfigs.some((feedConfig) => feeds[feedConfig.key]?.isLoading);

    return (
        <div>
            <Header />

            <section>
                <div className={`fw-main-row ${isLight ? "black-bg-content-LIGHT" : "black-bg-content"}`}>
                    <div className="fw-full-container-top" style={{ backgroundImage: "url(./images/bg-dots.png)" }}>
                        <div className="fw-container">
                            <div className="fw-row">
                                <div className="fw-col-xs-12 ">
                                    <div className="fw-divider-space"></div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center banner-seo-media-images">
                                <div className="seo-media-images-common">
                                    <img className="top-image-arrow-1" src="/images/Vector-line-straight.png" alt="" />
                                    <img className="top-image-arrow-2" src="/images/Vector-line-straight.png" alt="" />
                                </div>

                                <div className="seo-media-images-common">
                                    <img className="top-image-arrow-3" src="/images/Vector-line-straight.png" alt="" />
                                    <img className="top-image-arrow-4" src="/images/Vector-line-straight.png" alt="" />
                                </div>
                            </div>

                            <div className="content-under-image">
                                <div className="top-vector_main">
                                    <img className="vector-line-common" src="/images/Vector-upper-arrow.png" alt="" />
                                    <div className="fw-row">
                                        <div className="cector-cross-line-allign">
                                            <div className="vector-class-common">
                                                <div className="dots-first">
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                                <img className="vector-cross-common" src="/images/vector.png" alt="" />
                                                <div className="dots-second">
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                            </div>
                                            <div className="fw-heading fw-heading-h2 d-flex justify-content-center align-items-center">
                                                <h2 className={`${isLight ? "fw-special-title vector-heading-LIGHT" : "fw-special-title vector-heading"}`}>
                                                    Live Feed
                                                </h2>
                                            </div>
                                            <div className="vector-class-common">
                                                <div className="dots-first">
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                                <img className="vector-cross-common" src="/images/vector.png" alt="" />
                                                <div className="dots-second">
                                                    <div className="dot-common"></div>
                                                    <div className="dot-common"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <img className="vector-line-common1" src="/images/Vector-upper-arrow.png" alt="" />
                                </div>

                                <div className="fw-row">
                                    <div className="fw-col-xs-12 ">
                                        <div className="fw-divider-space" style={{ marginTop: "20px" }}></div>
                                    </div>
                                </div>
                                <div className="fw-row">
                                    <div className="container">
                                        <div className="fw-col-xs-12 ">
                                            <p className={`${isLight ? "heading-common-main-LIGHT" : "heading-common-main"}`}>Tech News</p>
                                            <p className={`${isLight ? "heading-common-content-LIGHT" : "heading-common-content"}`}>
                                                Browse autoplaying live headline sliders for technology and artificial intelligence.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="fw-row">
                                    <div className="fw-col-xs-12 ">
                                        <div className="fw-divider-space" style={{ marginTop: "50px" }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {feedConfigs.map((feedConfig) => (
                <FeedSliderSection
                    key={feedConfig.key}
                    feed={feeds[feedConfig.key]}
                    sectionTitle={feedConfig.feedName}
                    badge={feedConfig.badge}
                    emptyMessage={feedConfig.emptyMessage}
                />
            ))}

            {!isLoadingAnyFeed && visibleSections.length === 0 && (
                <section className="tech-news-empty">
                    <div className="container">
                        <p>No news feeds are available right now.</p>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
};

export default TechNews;
